import { Card, CardBody } from '@statsbomb/kitbag-components';
import { MetricValueMode } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useTranslation } from 'react-i18next';
import { unwrappedTeamDataDefaultColumnsAtom } from '@/atoms/dataLocker/team/table';
import { teamAggsFilterParamsAtom } from '@/atoms/queries/team/teamAggData';
import { metricValueModeAtom } from '@/atoms/table';
import { TableDataControls } from '@/components/controls/TableDataControls';
import { DataLockerTeamHighLevelFilters } from '@/components/dataLocker/filters/DataLockerTeamHighLevelFilters';
import { UserFilters } from '@/components/filters/UserFilters/UserFilters';
import { PageTitle } from '@/components/head/PageTitle';
import { LayoutWithFilters } from '@/components/layout/LayoutWithFilters';
import { OnboardingSettingsButton } from '@/components/onboarding/OnboardingSettingsButton';
import { AppearanceSettingsPanel } from '@/components/settings/AppearanceSettingsPanel';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TeamAggsTableWithPagination } from '@/components/tables/TeamAggsTable';
import { TEAM_AGGS_FILTER_ATOMS } from '@/consts/dataLocker/team';
import { dataLockerPageScopeAtoms } from '@/consts/scope';
import { useSetDataLockerDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetDataLockerDebouncedFilterParams';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useSelectedFilterSetName } from '@/hooks/userFilters/useSelectedFilterSetName';
import { teamsNormalisedAggregatesUrl, teamsTotalAggregatesUrl } from '@/query/url';
import { TableId } from '@/types/table';
import { EntityDataLockerContainer } from '../EntityDataLockerContainer';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const teamAggsFilterParams = useAtomValue(teamAggsFilterParamsAtom);
  const metricValueMode = useAtomValue(metricValueModeAtom);
  const urlCreationFunction =
    metricValueMode === MetricValueMode.Per90 ? teamsNormalisedAggregatesUrl : teamsTotalAggregatesUrl;
  useCsvDownload(pageTitle, urlCreationFunction(teamAggsFilterParams));
  return null;
};

const TableWithControls = () => {
  const { t } = useTranslation(['navigation']);

  return (
    <Card>
      <TableDataControls heading={t(`crossPlatform.secondary.teams`)}>
        <OnboardingSettingsButton />
      </TableDataControls>
      <CardBody>
        <TeamAggsTableWithPagination />
      </CardBody>
    </Card>
  );
};

const TableWithControlsWrapper = ({ pageTitle }: { pageTitle: string }) => (
  <EntityDataLockerContainer>
    <CsvDownload pageTitle={pageTitle} />
    <TableWithControls />
  </EntityDataLockerContainer>
);

const DebouncedFilterParams = () => {
  useSetDataLockerDebouncedFilterParams(TEAM_AGGS_FILTER_ATOMS);
  return null;
};

export const TeamDataLockerPage = () => {
  const { t } = useTranslation('entity');
  const pageTitle = t('team_other');
  const filtersTitle = useSelectedFilterSetName();

  return (
    <>
      <DebouncedFilterParams />
      <ScopeProvider atoms={dataLockerPageScopeAtoms}>
        <PageTitle>{pageTitle}</PageTitle>
        {/* NOTE - LayoutWithFilters needs to be inside ScopedTableWithDefaultColumns as it has a dependency on the order by field and order direction. */}
        <ScopedTableWithDefaultColumns
          defaultColumnsAtom={unwrappedTeamDataDefaultColumnsAtom}
          tableId={TableId.TEAM_AGGS}
        >
          <LayoutWithFilters
            filters={<DataLockerTeamHighLevelFilters />}
            filtersTitle={filtersTitle}
            filtersActions={<UserFilters />}
            settingsPanel={<AppearanceSettingsPanel />}
          >
            <TableWithControlsWrapper pageTitle={pageTitle} />
          </LayoutWithFilters>
        </ScopedTableWithDefaultColumns>
      </ScopeProvider>
    </>
  );
};
